import { PurchaseItem } from '../types/purchase';
import { WarehouseEntry } from '../types/warehouse';
import { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { ExternalUnitPriceSource } from './purchaseUnitPriceFix';

const normalizeNameForMatch = (value?: string): string => {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const stripMonthToken = (value?: string): string => {
  return (value || '')
    .replace(/\b(t|th|thang)\s*([1-9]|1[0-2])\b/gi, ' ')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const cleanupDisplayName = (value?: string): string => {
  return (value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildMatchKey = (name?: string): string => {
  const withoutMonth = stripMonthToken(name || '');
  const cleaned = cleanupDisplayName(withoutMonth || name || '');
  return normalizeNameForMatch(cleaned);
};

const normalizeCodeKey = (value?: string): string => {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
};

const normalizeUnitKey = (value?: string): string => {
  return normalizeNameForMatch(value)
    .replace(/\s+/g, '')
    .replace(/\./g, '');
};

const getMassUnitScale = (unit?: string): number | null => {
  const key = normalizeUnitKey(unit);
  if (!key) return null;
  if (['g', 'gram', 'gam'].includes(key)) return 1;
  if (['kg', 'kilo', 'kilogram'].includes(key)) return 1000;
  if (['l', 'lit', 'liter', 'litre'].includes(key)) return 1000;
  if (['ml', 'mililit', 'milliliter', 'millilitre'].includes(key)) return 1;
  return null;
};

const roundNumber = (value: number, precision = 6): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const resolveWarehouseUnitPrice = (entry: WarehouseEntry, targetUnit: string): number | null => {
  const targetScale = getMassUnitScale(targetUnit);
  if (entry.amountMode === 'per1000g' || normalizeUnitKey(entry.unit) === 'g') {
    if (!targetScale) return null;
    const perG = Number(entry.unitPrice || 0) / 1000;
    return roundNumber(perG * targetScale);
  }

  const entryUnitKey = normalizeUnitKey(entry.unit);
  const targetUnitKey = normalizeUnitKey(targetUnit);
  if (entryUnitKey && targetUnitKey && entryUnitKey === targetUnitKey) {
    return roundNumber(Number(entry.unitPrice || 0));
  }

  return null;
};

export const createWarehousePriceResolver = (
  goodsCatalog: WarehouseGoodsItem[],
  warehouseEntries: WarehouseEntry[],
) => {
  const latestImportById = new Map<string, WarehouseEntry>();
  warehouseEntries
    .filter((entry) => entry.entryType === 'import')
    .forEach((entry) => {
      const key = (entry.coffeeTypeId || '').trim();
      if (!key) return;
      const existing = latestImportById.get(key);
      const entryTime = (entry.updatedAt || entry.createdAt || new Date(0)).getTime();
      const existingTime = existing
        ? (existing.updatedAt || existing.createdAt || new Date(0)).getTime()
        : -1;
      if (!existing || entryTime > existingTime) {
        latestImportById.set(key, entry);
      }
    });

  const goodsIdByName = new Map<string, string>();
  const goodsIdByCode = new Map<string, string>();

  goodsCatalog.forEach((good) => {
    if (!good.id) return;
    const nameKey = buildMatchKey(good.name);
    if (nameKey && !goodsIdByName.has(nameKey)) {
      goodsIdByName.set(nameKey, good.id);
    }

    const codeKey = normalizeCodeKey(good.productCode);
    if (codeKey && !goodsIdByCode.has(codeKey)) {
      goodsIdByCode.set(codeKey, good.id);
    }
  });

  return (item: PurchaseItem): ExternalUnitPriceSource | null => {
    const warehouseId =
      (item.warehouseCoffeeId || '').trim() ||
      goodsIdByCode.get(normalizeCodeKey(item.code)) ||
      goodsIdByName.get(buildMatchKey(item.itemName)) ||
      '';

    if (!warehouseId) return null;

    const entry = latestImportById.get(warehouseId);
    if (!entry) return null;

    const unitPrice = resolveWarehouseUnitPrice(entry, item.unit);
    if (!unitPrice || unitPrice <= 0) return null;

    return {
      unitPrice,
      sourceItemId: entry.id,
      sourceItemName: entry.coffeeTypeName,
    };
  };
};
